import { getIdToken } from "../auth/AuthProvider.js";

/**
 * Fetch wrapper for the Ops BFF. Attaches the Cognito ID token as a Bearer
 * header (the /ops/* authorizer's audience is the SPA client id). Returns
 * parsed JSON for 2xx, throws ApiError otherwise.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export async function opsFetch<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) throw new Error("VITE_API_BASE_URL is not set in this build.");

  const token = await getIdToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = { ...options, headers };
  if (options.json !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.json);
  }

  const url = baseUrl.replace(/\/$/, "") + (path.startsWith("/") ? path : `/${path}`);
  const res = await fetch(url, init);
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(`API ${res.status} ${res.statusText}`, res.status, parsed);
  return parsed as T;
}
